import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, Activity, Bolt, Eye } from "lucide-react";
import { useParams } from "react-router-dom";

export default function DemandPage() {
  const { t, i18n } = useTranslation();
  const deviceType = localStorage.getItem("deviceType") || "analyzer";

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const { deviceId } = useParams();

  useEffect(() => {
    if (!deviceId) return;

    const token = localStorage.getItem("token");

    const ENDPOINT_PREFIX =
      deviceType === "multimeter"
        ? "/multimeter"
        : "/measurements";

    const url = `${API_BASE.replace(/\/$/, "")}${ENDPOINT_PREFIX}/demand?deviceId=${deviceId}`;


    async function fetchDemand() {
      try {
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        setData(json);
        setLastUpdate(new Date().toLocaleTimeString(i18n.language));
        setLoading(false);
      } catch (err) {
        console.error("❌ Fetch demand error:", err);
        setLoading(false);
      }
    }

    // ⏱ ilk çalıştır
    fetchDemand();

    // 🔁 5 saniyede bir yenile
    const interval = setInterval(fetchDemand, 5000);

    // 🧹 cleanup
    return () => clearInterval(interval);
  }, [API_BASE, deviceId]);

  const sections =
    deviceType === "multimeter"
      ? [
        {
          title: t('demand.phaseDemandValues'),
          icon: Activity,
          color: "#2196f3",
          keys: [
            { key: "IL1_DEMAND", label: t('demand.L1PhaseDemand'), unit: "A", max: 250 },
            { key: "IL2_DEMAND", label: t('demand.L2PhaseDemand'), unit: "A", max: 250 },
            { key: "IL3_DEMAND", label: t('demand.L3PhaseDemand'), unit: "A", max: 250 },
          ],
        },
        {
          title: t('demand.maxDemandValues'),
          icon: Eye,
          color: "#ff5252",
          keys: [
            { key: "IL1_DEMAND_MAX", label: t('demand.L1MaxDemand'), unit: "A", max: 250 },
            { key: "IL2_DEMAND_MAX", label: t('demand.L2MaxDemand'), unit: "A", max: 250 },
            { key: "IL3_DEMAND_MAX", label: t('demand.L3MaxDemand'), unit: "A", max: 250 },
          ],
        },
      ] :
      [
        {
          title: t('demand.currentDemandValues'),
          icon: Activity,
          color: "#2196f3",
          keys: [
            { key: "MB_ADD_R_DEMAND_I1", label: t('demand.I1Current'), unit: "A", max: 250 },
            { key: "MB_ADD_R_DEMAND_I2", label: t('demand.I2Current'), unit: "A", max: 250 },
            { key: "MB_ADD_R_DEMAND_I3", label: t('demand.I3Current'), unit: "A", max: 250 },
            { key: "MB_ADD_R_DEMAND_IN", label: t('demand.NeutralCurrent'), unit: "A", max: 100 },
          ],
        },
        {
          title: t('demand.activePowerDemandValues'),
          icon: Bolt,
          color: "#ffc107",
          keys: [
            { key: "MB_ADD_R_DEMAND_PT_IMPORT", label: t('demand.TotalImportPower'), unit: "W", max: 1000000 },
            { key: "MB_ADD_R_DEMAND_P1_IMPORT", label: t('demand.P1Import'), unit: "W", max: 350000 },
            { key: "MB_ADD_R_DEMAND_P2_IMPORT", label: t('demand.P2Import'), unit: "W", max: 350000 },
            { key: "MB_ADD_R_DEMAND_P3_IMPORT", label: t('demand.P3Import'), unit: "W", max: 350000 },
            { key: "MB_ADD_R_DEMAND_PT_EXPORT", label: t('demand.TotalExportPower'), unit: "W", max: 1000000 },
            { key: "MB_ADD_R_DEMAND_P1_EXPORT", label: t('demand.P1Export'), unit: "W", max: 350000 },
            { key: "MB_ADD_R_DEMAND_P2_EXPORT", label: t('demand.P2Export'), unit: "W", max: 350000 },
            { key: "MB_ADD_R_DEMAND_P3_EXPORT", label: t('demand.P3Export'), unit: "W", max: 350000 },
          ],
        },
        {
          title: t('demand.apparentPowerDemandValues'),
          icon: Eye,
          color: "#ff5252",
          keys: [
            { key: "MB_ADD_R_DEMAND_ST_IMPORT", label: t('demand.TotalImportPower'), unit: "VA", max: 1000000 },
            { key: "MB_ADD_R_DEMAND_S1_IMPORT", label: t('demand.S1Import'), unit: "VA", max: 350000 },
            { key: "MB_ADD_R_DEMAND_S2_IMPORT", label: t('demand.S2Import'), unit: "VA", max: 350000 },
            { key: "MB_ADD_R_DEMAND_S3_IMPORT", label: t('demand.S3Import'), unit: "VA", max: 350000 },
            { key: "MB_ADD_R_DEMAND_ST_EXPORT", label: t('demand.TotalExportPower'), unit: "VA", max: 1000000 },
            { key: "MB_ADD_R_DEMAND_S1_EXPORT", label: t('demand.S1Export'), unit: "VA", max: 350000 },
            { key: "MB_ADD_R_DEMAND_S2_EXPORT", label: t('demand.S2Export'), unit: "VA", max: 350000 },
            { key: "MB_ADD_R_DEMAND_S3_EXPORT", label: t('demand.S3Export'), unit: "VA", max: 350000 },
          ],
        },
      ];

  const Bar = ({ value = 0, max, color }) => {
    const ratio = Math.min(value / max, 1);
    return (
      <div className="relative w-full h-[14px] bg-[#2b2b2d] rounded-md overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full transition-all duration-500"
          style={{
            width: `${ratio * 100}%`,
            background: `linear-gradient(90deg, ${color}cc 0%, ${color} 100%)`,
            boxShadow: ratio > 0.8 ? `0 0 10px ${color}` : "none",
          }}
        ></div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#111] text-gray-100 flex flex-col items-center py-10 px-5">
      <div className="w-full max-w-6xl">
        {/* Üst başlık */}
        <div className="flex items-center gap-3 mb-6 border-b border-gray-700 pb-2">
          <BarChart3 className="w-6 h-6 text-blue-400" />
          <h1 className="text-2xl font-semibold tracking-wide text-gray-100">
            {t('demand.title')}
          </h1>
          {lastUpdate && (
            <span className="ml-auto text-sm text-gray-400">
              {t('demand.lastUpdate')}: <span className="text-blue-400">{lastUpdate}</span>
            </span>
          )}
        </div>

        {/* İçerik */}
        {loading ? (
          <p className="text-center text-gray-400 py-20">{t('demand.loading')}</p>
        ) : (
          <div className="flex flex-col gap-8">
            {sections.map((section, idx) => (
              <div
                key={idx}
                className="bg-[#1a1a1c] p-5 rounded-xl shadow-lg border border-gray-800"
              >
                <h2 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
                  <section.icon className="w-5 h-5" style={{ color: section.color }} />
                  {section.title}
                </h2>

                <div className="grid grid-cols-2 gap-5">
                  {section.keys.map((k) => {
                    const val = data[k.key] || 0;
                    return (
                      <div key={k.key}>
                        <div className="flex justify-between text-sm text-gray-400 mb-1">
                          <span>{k.label}</span>
                          <span className="font-mono text-gray-100">
                            {val.toFixed(2)} {k.unit}
                          </span>
                        </div>
                        <Bar value={val} max={k.max} color={section.color} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
